import { isValidElement, useEffect, useRef, useState, type ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PublicHeader } from "./PublicSite.js";

const API_MARKDOWN_URL = "/api.md";

/** A navigable heading in the canonical API reference. */
export type ApiDocsHeading = {
  /** DOM id used by both the rendered heading and its navigation link. */
  id: string;
  /** Heading level from the Markdown source. */
  level: 2 | 3;
  /** Plain-language label shown in the documentation index. */
  label: string;
};

/** Load state for the canonical public API Markdown file. */
type ApiDocsState =
  | { status: "loading" }
  | { status: "ready"; markdown: string }
  | { status: "error"; message: string };

/**
 * Converts a Markdown heading into a stable, URL-safe fragment.
 *
 * @param label - Heading text from the Markdown document.
 * @returns Fragment-safe base identifier.
 */
function createHeadingSlug(label: string): string {
  const slug = label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug === "" ? "section" : slug;
}

/**
 * Builds the left-hand navigation from level-two and level-three Markdown headings.
 *
 * @param markdown - Canonical API reference Markdown.
 * @returns Ordered, unique navigation headings.
 */
export function createApiDocsTableOfContents(markdown: string): ApiDocsHeading[] {
  const occurrences = new Map<string, number>();
  const headings: ApiDocsHeading[] = [];
  let insideCodeFence = false;

  for (const line of markdown.split("\n")) {
    if (/^\s*(```|~~~)/.test(line)) {
      insideCodeFence = !insideCodeFence;
      continue;
    }
    if (insideCodeFence) continue;
    const match = /^(##|###)\s+(.+?)\s*#*\s*$/.exec(line);
    if (match === null) continue;
    const level = match[1]?.length;
    const label = match[2]?.replace(/[`*_]/g, "").trim();
    if ((level !== 2 && level !== 3) || label === undefined || label === "") {
      continue;
    }
    const baseId = createHeadingSlug(label);
    const occurrence = occurrences.get(baseId) ?? 0;
    occurrences.set(baseId, occurrence + 1);
    headings.push({
      id: occurrence === 0 ? baseId : `${baseId}-${occurrence + 1}`,
      level,
      label,
    });
  }

  return headings;
}

/**
 * Extracts visible text from Markdown-rendered React children.
 *
 * @param children - React children inside a Markdown heading.
 * @returns Plain-text heading label.
 */
function getHeadingText(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(getHeadingText).join("");
  }
  if (isValidElement<{ children?: ReactNode }>(children)) {
    return getHeadingText(children.props.children);
  }
  return "";
}

/**
 * Renders the public API documentation from the same Markdown file exposed to LLMs.
 *
 * @returns Public API documentation page.
 */
export function ApiDocs(): React.JSX.Element {
  const [docs, setDocs] = useState<ApiDocsState>({ status: "loading" });
  const [copyLabel, setCopyLabel] = useState("Copy Markdown");
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const contentRef = useRef<HTMLElement>(null);
  const headings = docs.status === "ready" ? createApiDocsTableOfContents(docs.markdown) : [];
  const renderedHeadingCounts = new Map<string, number>();

  /**
   * Claims the next document heading id for a rendered Markdown heading.
   *
   * @param level - Rendered Markdown heading level.
   * @param label - Rendered plain-text heading label.
   * @returns Matching navigation fragment, when the heading is indexed.
   */
  const getRenderedHeadingId = (level: 2 | 3, label: string): string | undefined => {
    const matchingHeadings = headings.filter(
      (heading) => heading.level === level && heading.label === label,
    );
    const occurrence = renderedHeadingCounts.get(`${level}:${label}`) ?? 0;
    renderedHeadingCounts.set(`${level}:${label}`, occurrence + 1);
    return matchingHeadings[occurrence]?.id;
  };

  useEffect(() => {
    document.title = "PaperTiger API Documentation";
    const description = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]',
    );
    if (description !== null) {
      description.content =
        "Build paper-trading integrations with the public PaperTiger prediction-market API.";
    }
    const canonical = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (canonical !== null) {
      canonical.href = "https://papertigerapp.com/docs";
    }
    const controller = new AbortController();
    void fetch(API_MARKDOWN_URL, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`The API documentation returned ${response.status}.`);
        }
        return response.text();
      })
      .then((markdown) => setDocs({ status: "ready", markdown }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setDocs({
          status: "error",
          message:
            error instanceof Error
              ? error.message
              : "The API documentation could not be loaded.",
        });
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (docs.status !== "ready" || headings.length === 0) return;
    const headingElements = headings
      .map(({ id }) => document.getElementById(id))
      .filter((element): element is HTMLElement => element !== null);
    const hashId = window.location.hash.slice(1);
    setActiveHeadingId(
      headings.some(({ id }) => id === hashId) ? hashId : headings[0]?.id ?? null,
    );
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleHeading = entries
          .filter((entry) => entry.isIntersecting)
          .sort((first, second) => first.boundingClientRect.top - second.boundingClientRect.top)[0];
        if (visibleHeading !== undefined) {
          setActiveHeadingId(visibleHeading.target.id);
        }
      },
      { rootMargin: "-18% 0px -72% 0px", threshold: [0, 1] },
    );
    headingElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [docs, headings]);

  /**
   * Copies the canonical Markdown source to the clipboard.
   *
   * @returns Nothing.
   */
  const copyMarkdown = async (): Promise<void> => {
    if (docs.status !== "ready") return;
    await navigator.clipboard.writeText(docs.markdown);
    setCopyLabel("Copied");
    window.setTimeout(() => setCopyLabel("Copy Markdown"), 1_800);
  };

  return (
    <div className="docs-shell">
      <PublicHeader />
      <div className="docs-header">
        <strong>API documentation</strong>
        <nav aria-label="API documentation actions">
          <a className="secondary docs-action" href={API_MARKDOWN_URL}>
            View Markdown
          </a>
          <button
            className="primary docs-action"
            type="button"
            disabled={docs.status !== "ready"}
            onClick={() => void copyMarkdown()}
          >
            {copyLabel}
          </button>
        </nav>
      </div>
      <div className="docs-layout">
        {docs.status === "ready" && (
          <nav className="docs-toc" aria-label="API documentation sections">
            <strong>On this page</strong>
            <ol>
              {headings.map((heading) => (
                <li key={heading.id} className={`docs-toc-level-${heading.level}`}>
                  <a
                    href={`#${heading.id}`}
                    aria-current={activeHeadingId === heading.id ? "location" : undefined}
                    onClick={() => setActiveHeadingId(heading.id)}
                  >
                    {heading.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}
        <main ref={contentRef} className="docs-content surface">
        {docs.status === "loading" && (
          <p className="docs-status">Loading API documentation…</p>
        )}
        {docs.status === "error" && (
          <div className="notice error">
            {docs.message}{" "}
            <a href={API_MARKDOWN_URL}>Open the Markdown file directly.</a>
          </div>
        )}
        {docs.status === "ready" && (
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children, ...properties }) => {
                const id = getRenderedHeadingId(2, getHeadingText(children));
                return <h2 {...properties} id={id}>{children}</h2>;
              },
              h3: ({ children, ...properties }) => {
                const id = getRenderedHeadingId(3, getHeadingText(children));
                return <h3 {...properties} id={id}>{children}</h3>;
              },
            }}
          >
            {docs.markdown}
          </Markdown>
        )}
        </main>
      </div>
    </div>
  );
}
